/**
 * Transport layer for the GA4 ecommerce funnel.
 *
 * The mapper (`ga4-ecommerce.ts`) builds one canonical payload per event and this module decides
 * where it goes. Nothing here mutates the payload, and no ecommerce `gtag` call lives outside it.
 */

/** Official GA4 ecommerce event names. Only these reach Analytics. */
export type Ga4EcommerceEventName =
  | 'view_item_list'
  | 'select_item'
  | 'view_item'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'add_payment_info'
  | 'purchase';

export interface IGa4Item {
  item_id: string;
  item_name: string;
  affiliation?: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  /** Net unit price, already carrying the item configuration and its share of the discount. */
  price: number;
  /** Unit discount allocated to this entry. */
  discount?: number;
  /** Quantity moved by the event, never the whole cart line. */
  quantity: number;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
  coupon?: string;
  food_flavors?: string;
  food_crust?: string;
  food_edge?: string;
  food_add_ons?: string;
  /** Only on the aggregate entry that replaces the items dropped past the 200 item cap. */
  overflow_item_count?: number;
}

export interface IGa4EcommercePayload {
  currency?: string;
  value?: number;
  coupon?: string;
  shipping?: number;
  transaction_id?: string;
  payment_type?: string;
  shipping_tier?: string;
  item_list_id?: string;
  item_list_name?: string;
  /** Diagnostics for lists split across events, meant for DebugView and BigQuery. */
  batch_index?: number;
  batch_count?: number;
  items: IGa4Item[];
}

/**
 * Single envelope for every ecommerce event mirrored to a Google Tag Manager container. One custom
 * event trigger and one GA4 tag configure the whole funnel, and the native `gtag('event', ...)`
 * command — which writes to the same `dataLayer` — cannot accidentally fire that trigger.
 */
export const GA4_DATA_LAYER_EVENT = 'bsfood_ecommerce';

export type IBsFoodEcommerceDataLayerEvent = {
  event: typeof GA4_DATA_LAYER_EVENT;
  bsfood_event_name: Ga4EcommerceEventName;
  ecommerce: IGa4EcommercePayload;
};

/** Tracking ids configured for the tenant. Absent means the channel is not installed. */
export interface ITrackingDestinations {
  ga4Id?: string;
  gtmId?: string;
}

/**
 * Which channels took the event. "Enqueued" and not "delivered": neither `gtag` nor `dataLayer`
 * confirms that the vendor processed anything.
 */
export interface IGa4EmissionResult {
  ga4Enqueued: boolean;
  gtmEnqueued: boolean;
}

export const dispatchGa4Ecommerce = (
  name: Ga4EcommerceEventName,
  payload: IGa4EcommercePayload,
  { ga4Id, gtmId }: ITrackingDestinations,
): IGa4EmissionResult => {
  const result: IGa4EmissionResult = { ga4Enqueued: false, gtmEnqueued: false };

  // Each channel is governed by the tenant configuration, never by the presence of the global
  // alone: an unrelated script can define window.gtag or create window.dataLayer, and firing in
  // that case would send tenant data to a destination nobody configured.
  if (ga4Id && window.gtag) {
    // send_to is routing, not ecommerce data, so it stays beside the payload and never inside it.
    window.gtag('event', name, { ...payload, send_to: ga4Id });
    result.ga4Enqueued = true;
  }

  if (gtmId && window.dataLayer) {
    const event: IBsFoodEcommerceDataLayerEvent = {
      event: GA4_DATA_LAYER_EVENT,
      bsfood_event_name: name,
      ecommerce: payload,
    };

    // Clearing first is mandatory: without it the container merges the previous ecommerce object
    // and parameters such as coupon, shipping or items leak into the following event.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
    result.gtmEnqueued = true;
  }

  return result;
};
